import React from "react";
import { supabase } from "../../../clients/supabaseClient";
import { XMarkIcon, PhotoIcon } from "@heroicons/react/24/outline";

type Category = { id: string; name: string };
type Store = { id: string; name: string };

type Product = {
  id?: string;
  sku: string;
  name: string;
  current_price: string | number;
  stock: number;
  category_ids?: string[];
  store_id?: string | null;
  is_active?: boolean;
  is_special?: boolean;
  description?: string | null;
  image_url?: string | null;
  specification?: string | null;
  keywords?: string[] | null;
};

function AlertModal({title, message, onClose,}: { title: string; message: string; onClose: () => void; }) {
  return (
      <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
        <div className="bg-white w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden">
          <div className="h-1.5 bg-[#e6186c] w-full" />
          <div className="p-6 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-50 mb-4">
              <XMarkIcon className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-stone-900">{title}</h3>
            <p className="mt-2 text-sm text-stone-500 leading-relaxed">{message}</p>
            <button
                onClick={onClose}
                className="mt-6 w-full inline-flex justify-center rounded-lg bg-[#e6186c] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#d01560] transition-colors shadow-sm"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
  );
}

export default function ProductForm({initial, onClose, onSaved,}: {
  initial?: Product;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [stores, setStores] = React.useState<Store[]>([]);
  const [saving, setSaving] = React.useState(false);
  const [errorStatus, setErrorStatus] = React.useState<{ title: string; msg: string } | null>(null);

  const [form, setForm] = React.useState<Product>({
    id: initial?.id,
    sku: initial?.sku ?? "",
    name: initial?.name ?? "",
    current_price: initial?.current_price ?? "0.00",
    stock: initial?.stock ?? 0,
    category_ids: initial?.category_ids ?? [],
    store_id: initial?.store_id ?? null,
    is_active: initial?.is_active ?? true,
    is_special: initial?.is_special ?? false,
    description: initial?.description ?? "",
    image_url: initial?.image_url ?? "",
    specification: initial?.specification ?? "",
    keywords: initial?.keywords ?? [],
  });

  const [keywordsText, setKeywordsText] = React.useState(
      Array.isArray(initial?.keywords) ? initial.keywords.join(", ") : ""
  );

  React.useEffect(() => {
    if (initial) {
      const rawPrice =
          (initial as any).current_price ??
          (initial as any).display_price ??
          0;
      const formattedPrice = (Number(rawPrice) / 100).toFixed(2);

      setForm({
        id: initial.id,
        sku: initial.sku ?? "",
        name: initial.name ?? "",
        current_price: formattedPrice,
        stock: (initial as any).stock ?? 0,
        store_id: (initial as any).store_id ?? null,
        category_ids: initial.category_ids ?? [],
        is_active: initial.is_active ?? true,
        is_special: initial.is_special ?? false,
        description: initial.description ?? "",
        image_url: initial.image_url ?? "",
        specification: initial.specification ?? "",
        keywords: initial.keywords ?? [],
      });

      setKeywordsText(Array.isArray(initial.keywords) ? initial.keywords.join(", ") : "");
    }
  }, [initial]);

  React.useEffect(() => {
    const loadFormData = async () => {
      const [catRes, storeRes] = await Promise.all([
        supabase.from("categories").select("id,name").order("name"),
        supabase.from("stores").select("id,name").order("name"),
      ]);

      setCategories(catRes.data ?? []);
      setStores(storeRes.data ?? []);

      if (initial?.id) {
        const { data: assignedCategories } = await supabase
            .from("category_products")
            .select("category_id")
            .eq("product_id", initial.id);

        setForm((prev) => ({
          ...prev,
          category_ids: assignedCategories?.map((row) => row.category_id) ?? [],
        }));
      }
    };

    loadFormData();
  }, [initial?.id]);

  const handleChange = (k: keyof Product, v: any) =>
      setForm((prev) => ({ ...prev, [k]: v }));

  const toggleCategory = (categoryId: string) => {
    setForm((prev) => {
      const current = prev.category_ids ?? [];
      const exists = current.includes(categoryId);

      return {
        ...prev,
        category_ids: exists
            ? current.filter((id) => id !== categoryId)
            : [...current, categoryId],
      };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleanSku = form.sku.trim().toUpperCase();
      const priceInCents = Math.round(parseFloat(String(form.current_price || 0)) * 100);

      const keywordsArray = keywordsText
          .split(",")
          .map((k) => k.trim().toLowerCase())
          .filter(Boolean)
          .filter((value, index, arr) => arr.indexOf(value) === index);

      const productPayload = {
        sku: cleanSku,
        name: form.name.trim(),
        is_active: !!form.is_active,
        is_special: !!form.is_special,
        description: form.description || null,
        specification: form.specification || null,
        image_url: form.image_url || null,
        keywords: keywordsArray,
      };

      const res = form.id
          ? await supabase.from("products").update(productPayload).eq("id", form.id).select().single()
          : await supabase.from("products").insert(productPayload).select().single();

      if (res.error) throw res.error;
      const savedProduct = res.data;

      if (!savedProduct?.id) throw new Error("No product ID returned.");

      if (form.store_id) {
        const nowIso = new Date().toISOString();

        const { data: activePrice, error: activePriceError } = await supabase
            .from("product_prices")
            .select("id, price")
            .eq("product_id", savedProduct.id)
            .eq("store_id", form.store_id)
            .lte("valid_from", nowIso)
            .or(`valid_to.gt.${nowIso},valid_to.is.null`)
            .maybeSingle();

        if (activePriceError) throw activePriceError;

        if (!activePrice || Number(activePrice.price) !== priceInCents) {
          if (activePrice) {
            const { error: closePriceError } = await supabase
                .from("product_prices")
                .update({ valid_to: nowIso })
                .eq("id", activePrice.id);

            if (closePriceError) throw closePriceError;
          }

          const { error: insertPriceError } = await supabase.from("product_prices").insert({
            product_id: savedProduct.id,
            store_id: form.store_id,
            price: priceInCents,
            valid_from: nowIso,
            valid_to: null,
          });

          if (insertPriceError) throw insertPriceError;
        }

        const { error: upsertStoreError } = await supabase.from("products_store").upsert(
            {
              product_id: savedProduct.id,
              store_id: form.store_id,
              stock: Number(form.stock),
            },
            { onConflict: "product_id,store_id" }
        );

        if (upsertStoreError) throw upsertStoreError;
      }

      const { data: existingLinks, error: existingLinksError } = await supabase
          .from("category_products")
          .select("category_id")
          .eq("product_id", savedProduct.id);

      if (existingLinksError) throw existingLinksError;

      const existingIds = existingLinks?.map((row) => row.category_id) || [];
      const selectedIds = Array.from(new Set(form.category_ids || []));

      const toAdd = selectedIds.filter((id) => !existingIds.includes(id));
      const toRemove = existingIds.filter((id) => !selectedIds.includes(id));

      if (toRemove.length > 0) {
        const { error: removeCategoryError } = await supabase
            .from("category_products")
            .delete()
            .eq("product_id", savedProduct.id)
            .in("category_id", toRemove);

        if (removeCategoryError) throw removeCategoryError;
      }

      if (toAdd.length > 0) {
        const categoryLinks = toAdd.map((categoryId) => ({
          product_id: savedProduct.id,
          category_id: categoryId,
        }));

        const { error: addCategoryError } = await supabase
            .from("category_products")
            .insert(categoryLinks);

        if (addCategoryError) throw addCategoryError;
      }

      if (form.is_special) {
        const { data: existingSpecial, error: specialLookupError } = await supabase
            .from("specials")
            .select("id")
            .eq("link_target", savedProduct.id)
            .maybeSingle();

        if (specialLookupError) throw specialLookupError;

        const specialPayload = {
          title: savedProduct.name,
          image_url: savedProduct.image_url || "",
          link_type: "product",
          link_target: savedProduct.id,
          active: true,
          homepage: false,
          sort_order: 0,
          description: "",
        };

        if (existingSpecial) {
          const { error: updateSpecialError } = await supabase
              .from("specials")
              .update(specialPayload)
              .eq("id", existingSpecial.id);

          if (updateSpecialError) throw updateSpecialError;
        } else {
          const { error: insertSpecialError } = await supabase
              .from("specials")
              .insert(specialPayload);

          if (insertSpecialError) throw insertSpecialError;
        }
      } else {
        const { error: deleteSpecialError } = await supabase
            .from("specials")
            .delete()
            .eq("link_target", savedProduct.id);

        if (deleteSpecialError) throw deleteSpecialError;
      }

      setSaving(false);
      onSaved();
      onClose();
    } catch (error: any) {
      console.error("❌ Product Save Error:", error.message || error);
      setSaving(false);
      setErrorStatus({
        title: "Could not save product",
        msg: error.message || "Something went wrong while saving the product.",
      });
    }
  };

  const [searchTerm, setSearchTerm] = React.useState("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const filteredCategories = categories
      .filter((cat) => cat.name.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => {
        const aSelected = (form.category_ids ?? []).includes(a.id);
        const bSelected = (form.category_ids ?? []).includes(b.id);
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return 0;
      });

  return (
      <>
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-sm max-h-[90vh] overflow-y-auto">
            <div className="h-1 bg-[#e6186c] w-full" />

            <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold text-stone-900">
                {form.id ? "Edit Product" : "Add Product"}
              </h2>
              <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-600 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">SKU</label>
                  <input
                      className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#e6186c] bg-white"
                      value={form.sku}
                      onChange={(e) => handleChange("sku", e.target.value)}
                      placeholder="e.g., SKU-001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Name</label>
                  <input
                      className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#e6186c] bg-white"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Product name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Price (Rands)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-stone-400 text-sm">R</span>
                    <input
                        type="number"
                        step="0.01"
                        className="w-full border border-stone-300 rounded-lg pl-7 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#e6186c] bg-white"
                        value={form.current_price}
                        onChange={(e) => handleChange("current_price", e.target.value)}
                        placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Stock</label>
                  <input
                      type="number"
                      className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#e6186c] bg-white"
                      value={form.stock}
                      onChange={(e) => handleChange("stock", Number(e.target.value))}
                      placeholder="0"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Keywords</label>
                  <textarea
                      className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#e6186c] bg-white"
                      rows={3}
                      value={keywordsText}
                      onChange={(e) => setKeywordsText(e.target.value)}
                      placeholder="comma separated keywords, e.g. coke, cola, soft drink, cooldrink"
                  />
                  <p className="mt-1 text-xs text-stone-500">
                    Separate keywords with commas. These will update the product search index.
                  </p>
                </div>

                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-stone-700">Categories</label>
                    <span className="text-[10px] font-medium px-2 py-0.5 bg-stone-100 text-stone-500 rounded-full">
                                        {(form.category_ids ?? []).length} Selected
                                    </span>
                  </div>
                  <div className="relative mb-2">
                    <input
                        type="text"
                        placeholder="Search categories..."
                        className="w-full border border-stone-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#e6186c]/20 bg-white"
                        onChange={(e) => setSearchTerm(e.target.value)}
                        value={searchTerm}
                    />
                    <div className="absolute left-3 top-2.5 text-stone-400">
                      <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="w-4 h-4"
                      >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                        />
                      </svg>
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto rounded-lg border border-stone-200 bg-stone-50 p-2 space-y-1">
                    {filteredCategories.map((c) => (
                        <label
                            key={c.id}
                            className={`flex items-center gap-3 rounded-md px-3 py-2 border transition-colors cursor-pointer ${
                                (form.category_ids ?? []).includes(c.id)
                                    ? "bg-[#e6186c]/5 border-[#e6186c]/20"
                                    : "bg-white border-stone-200 hover:border-stone-300"
                            }`}
                        >
                          <input
                              type="checkbox"
                              checked={(form.category_ids ?? []).includes(c.id)}
                              onChange={() => toggleCategory(c.id)}
                              className="h-4 w-4 rounded border-stone-300 text-[#e6186c] accent-[#e6186c]"
                          />
                          <span className="text-sm text-stone-700 font-medium">{c.name}</span>
                        </label>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-stone-500">
                    Categories are saved via <code>category_products</code> and your category triggers should refresh <code>search_fts</code>.
                  </p>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Store Assignment</label>
                  <select
                      className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#e6186c] bg-white"
                      value={form.store_id ?? ""}
                      onChange={(e) => handleChange("store_id", e.target.value || null)}
                  >
                    <option value="">— Select store —</option>
                    {stores.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4 col-span-2">
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-2">Status</label>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-stone-50 border border-stone-200 h-[46px]">
                      <input
                          type="checkbox"
                          id="is_active"
                          checked={!!form.is_active}
                          onChange={(e) => handleChange("is_active", e.target.checked)}
                          className="h-4 w-4 rounded border-stone-300 text-[#e6186c] accent-[#e6186c]"
                      />
                      <label htmlFor="is_active" className="text-sm font-medium text-stone-700">
                        Active
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-2">Promotion</label>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-stone-50 border border-stone-200 h-[46px]">
                      <input
                          type="checkbox"
                          id="is_special"
                          checked={!!form.is_special}
                          onChange={(e) => handleChange("is_special", e.target.checked)}
                          className="h-4 w-4 rounded text-[#e6186c] accent-[#e6186c]"
                      />
                      <label htmlFor="is_special" className="text-sm font-medium text-stone-700">
                        Mark as Special
                      </label>
                    </div>
                  </div>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Specification</label>
                  <textarea
                      className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#e6186c] bg-white"
                      rows={3}
                      value={form.specification ?? ""}
                      onChange={(e) => handleChange("specification", e.target.value)}
                      placeholder="Enter product specifications…"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Description (optional)</label>
                  <textarea
                      className="w-full border border-stone-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#e6186c] bg-white"
                      rows={3}
                      value={form.description ?? ""}
                      onChange={(e) => handleChange("description", e.target.value)}
                      placeholder="Enter product description…"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-stone-700 mb-2">Product Image</label>
                  {form.image_url && (
                      <div className="mb-3 relative inline-block">
                        <img
                            src={form.image_url}
                            alt="Preview"
                            className="w-20 h-20 object-cover rounded-lg border border-stone-200"
                        />
                        <button
                            type="button"
                            onClick={() => handleChange("image_url", "")}
                            className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow hover:bg-red-700 transition-colors"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </div>
                  )}
                  <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;

                        const ext = file.name.split(".").pop();
                        const filePath = `${Date.now()}.${ext}`;

                        const { error: uploadError } = await supabase.storage
                            .from("product_images")
                            .upload(filePath, file, { upsert: true });

                        if (uploadError) {
                          setErrorStatus({
                            title: "Image upload failed",
                            msg: uploadError.message,
                          });
                          return;
                        }

                        const { data: publicUrl } = supabase.storage
                            .from("product_images")
                            .getPublicUrl(filePath);

                        handleChange("image_url", publicUrl.publicUrl);
                      }}
                  />
                  <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 transition-colors"
                  >
                    <PhotoIcon className="h-4 w-4" />
                    {form.image_url ? "Change Image" : "Upload Image"}
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-stone-200">
                <button
                    onClick={onClose}
                    className="px-4 py-2.5 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                    onClick={save}
                    disabled={saving}
                    className="px-4 py-2.5 rounded-lg bg-[#e6186c] text-white text-sm font-medium hover:bg-[#d01560] transition-colors disabled:opacity-60"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {errorStatus && (
            <AlertModal
                title={errorStatus.title}
                message={errorStatus.msg}
                onClose={() => setErrorStatus(null)}
            />
        )}
      </>
  );
}